import * as R from 'ramda';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  Dimension,
  ProductVariation,
} from 'containers/Forms/matrixVariations/types';
import useProducts from 'utils/hooks/useProducts';
import { getSelectedWarehouseID } from 'reducers/warehouses';
import { getSetting } from 'reducers/configs/settings';

const dimensionProps = ['dimensionID', 'dimensionIndex', 'name', 'itemIndex'];
const dimensionsFromVariations = (
  variations: ProductVariation[] = [],
): Dimension[] =>
  R.pipe(
    // Take dimensions from the products
    R.map(R.prop('dimensions')),
    R.flatten,
    R.addIndex(R.map)((obj, itemIndex) => ({ ...obj, itemIndex })),
    // Group by dimension
    R.groupBy(R.prop('dimensionID')),
    R.values,
    // Extract dimension props from first object, leave option-specific ones in values
    R.map(arr => ({
      ...R.pick(dimensionProps)(arr[0]),
      values: R.pipe(
        R.map(R.omit(dimensionProps)),
        R.uniq,
        R.sort(R.ascend(R.prop('order'))),
      )(arr),
    })),
    // Sort by index
    R.sort(R.descend(R.prop('itemIndex'))),
  )(variations);

export const useMatrixDimensions = (variations: ProductVariation[]) =>
  useMemo(() => dimensionsFromVariations(variations), [variations]);

export const useAvailableMatrixDimensions = (
  variations: ProductVariation[] = [],
) => {
  const variationIDs = useMemo(
    () => variations.map(v => v.productID).map(Number),
    [variations],
  );
  const warehouseID = useSelector(getSelectedWarehouseID);

  const getItemsFromFirstPriceListOnly = useSelector(
    getSetting('pos_allow_selling_only_from_pricelist1'),
  );

  const products = useProducts({
    warehouseID,
    productIDs: variationIDs,
    recordsOnPage: 10000,
    getProductsFor: 'SALES',
    getItemsFromFirstPriceListOnly,
  });

  const dimensions = useMatrixDimensions(variations);
  return { dimensions, products };
};
